import { useState, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw, Save, Activity, Plug, Power, Clock, Workflow, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { api } from '@/lib/api';

interface CrawlerConfig {
  mode: 'self' | 'third_party';
  thirdPartyUrl: string;
  thirdPartyToken: string;
  proxyPool: string;
  cronHour: number;
  cronMinute: number;
  dailyLimit: number;
  softWarnRatio: number;
  minIntervalSec: number;
}

interface SafetyState {
  date: string;
  used: number;
  limit: number;
  softWarnAt: number;
  minIntervalSec: number;
  secondsSinceLast: number;
  warning: boolean;
  blocked: boolean;
  reason: string;
}

interface PipelineItem {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  cronHour: number;
  cronMinute: number;
  lastRunAt: string | null;
  dailyUsed: number;
  dailyLimit: number;
  status: 'healthy' | 'warning' | 'blocked' | 'disabled';
}

export function CrawlerAdminPage() {
  const qc = useQueryClient();
  const { data: config } = useQuery<CrawlerConfig>({
    queryKey: ['crawler', 'config'],
    queryFn: () => api.get('crawler/config').json<CrawlerConfig>(),
  });
  const { data: safety } = useQuery<SafetyState>({
    queryKey: ['crawler', 'safety'],
    queryFn: () => api.get('crawler/safety').json<SafetyState>(),
    refetchInterval: 30_000,
  });

  const [form, setForm] = useState<CrawlerConfig | null>(null);
  const effective = form ?? config ?? null;

  const save = useMutation({
    mutationFn: (body: CrawlerConfig) =>
      api.put('crawler/config', { json: body }).json<CrawlerConfig>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crawler', 'config'] });
      setForm(null);
    },
  });

  const runNow = useMutation({
    mutationFn: (force: boolean) =>
      api.post('crawler/run', { searchParams: force ? { force: 'true' } : {} }).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['radar', 'topics'] });
      qc.invalidateQueries({ queryKey: ['crawler', 'safety'] });
      qc.invalidateQueries({ queryKey: ['crawler', 'pipelines'] });
    },
  });

  const { data: pipelines } = useQuery<PipelineItem[]>({
    queryKey: ['crawler', 'pipelines'],
    queryFn: () => api.get('crawler/pipelines').json<PipelineItem[]>(),
    refetchInterval: 30_000,
  });

  const togglePipeline = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api
        .put(`crawler/pipelines/${id}/toggle`, { json: { enabled } })
        .json<PipelineItem>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crawler', 'pipelines'] });
    },
  });

  const ratio = safety ? safety.used / Math.max(1, safety.limit) : 0;
  const bar = useMemo(() => {
    if (!safety) return 'bg-neutral-200';
    if (safety.blocked) return 'bg-danger';
    if (safety.warning) return 'bg-warning';
    return 'bg-success';
  }, [safety]);

  // 二次确认状态:流水线开关 / 强制执行
  const [pipelineConfirm, setPipelineConfirm] = useState<{ id: string; nextEnabled: boolean } | null>(null);
  const [forceConfirm, setForceConfirm] = useState(false);

  if (!effective) {
    return <div className="p-8 text-sm text-neutral-500">加载爬虫配置…</div>;
  }

  const set = <K extends keyof CrawlerConfig>(k: K, v: CrawlerConfig[K]) =>
    setForm({ ...(form ?? effective), [k]: v });

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <header className="flex items-end justify-between gap-4">
        <div>
          <nav className="flex items-center gap-1 text-xs text-neutral-500">
            <Link to="/admin" className="hover:text-brand-500">
              管理员
            </Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/admin/scripts" className="hover:text-brand-500">
              脚本采集
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-neutral-700">热点抓取与 IP 化</span>
          </nav>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight">热点抓取与 IP 化</h1>
          <p className="mt-1 text-sm text-neutral-600">
            自主抓取 / 第三方接入 · IP 代理池 · 安全频率与阈值
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => runNow.mutate(false)}
            disabled={runNow.isPending || (safety?.blocked ?? false)}
            title={safety?.blocked ? safety.reason : '手动触发一次抓取'}
          >
            <RefreshCw className={runNow.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            手动抓取
          </Button>
          <Button
            variant="agent"
            agentColor="radar"
            size="sm"
            onClick={() => save.mutate(form ?? effective)}
            disabled={!form || save.isPending}
          >
            <Save className="h-4 w-4" />
            保存配置
          </Button>
        </div>
      </header>

      {/* 安全状态卡片 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-4 w-4" /> 今日抓取配额
            </CardTitle>
            {safety?.blocked ? (
              <Badge variant="danger">已达硬阈值 · 阻断</Badge>
            ) : safety?.warning ? (
              <Badge variant="warning">接近阈值 · 软警告</Badge>
            ) : (
              <Badge variant="success">健康</Badge>
            )}
          </div>
          <CardDescription>
            {safety
              ? `${safety.used}/${safety.limit} 次 · 距上次 ${safety.secondsSinceLast}s · 最小间隔 ${safety.minIntervalSec}s`
              : '加载中…'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-2 w-full overflow-hidden rounded-full bg-surface-variant">
            <div
              className={`h-full ${bar} transition-all`}
              style={{ width: `${Math.min(100, ratio * 100).toFixed(1)}%` }}
            />
          </div>
          {safety?.reason && (
            <div className="mt-3 flex items-start gap-2 rounded-md bg-warning/10 p-3 text-xs text-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>{safety.reason}</div>
            </div>
          )}
          {safety?.blocked && (
            <Button
              size="sm"
              variant="ghost"
              className="mt-3"
              onClick={() => setForceConfirm(true)}
            >
              强制执行(忽略阈值)
            </Button>
          )}
        </CardContent>
      </Card>

      {/* 自动抓取流水线 · 可扩展列表(当前仅热点抓取与IP化) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Workflow className="h-4 w-4" /> 自动抓取流水线
          </CardTitle>
          <CardDescription>
            管理所有定时执行的爬虫流水线 · 可单独开启/关闭进程,关闭后 Celery beat 自动跳过该任务
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(pipelines ?? []).map((p) => (
            <PipelineRow
              key={p.id}
              item={p}
              toggling={togglePipeline.isPending}
              onToggle={(enabled) => setPipelineConfirm({ id: p.id, nextEnabled: enabled })}
            />
          ))}
          {pipelines && pipelines.length === 0 && (
            <div className="rounded-md border border-dashed border-outline-variant p-6 text-center text-xs text-neutral-500">
              暂无注册的流水线
            </div>
          )}
        </CardContent>
      </Card>

      {/* 模式切换 */}
      <Tabs value={effective.mode} onValueChange={(v) => set('mode', v as 'self' | 'third_party')}>
        <TabsList>
          <TabsTrigger value="self">自主抓取</TabsTrigger>
          <TabsTrigger value="third_party">自定义第三方</TabsTrigger>
        </TabsList>

        <TabsContent value="self">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">自主抓取数据源</CardTitle>
              <CardDescription>
                B 站 / 微博 / 知乎 / 百度 / 头条 · 抖音需 cookie · LLM 自动 9 维评分
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field label="代理池(可选,逗号分隔多 URL)" hint="http://user:pass@host:port,...">
                <Input
                  value={effective.proxyPool}
                  onChange={(e) => set('proxyPool', e.target.value)}
                  placeholder="留空 = 不走代理"
                />
              </Field>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Field label="定时小时">
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={effective.cronHour}
                    onChange={(e) => set('cronHour', Number(e.target.value))}
                  />
                </Field>
                <Field label="定时分钟">
                  <Input
                    type="number"
                    min={0}
                    max={59}
                    value={effective.cronMinute}
                    onChange={(e) => set('cronMinute', Number(e.target.value))}
                  />
                </Field>
                <Field label="每日上限">
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={effective.dailyLimit}
                    onChange={(e) => set('dailyLimit', Number(e.target.value))}
                  />
                </Field>
                <Field label="软警阈值占比">
                  <Input
                    type="number"
                    step="0.05"
                    min={0.1}
                    max={1}
                    value={effective.softWarnRatio}
                    onChange={(e) => set('softWarnRatio', Number(e.target.value))}
                  />
                </Field>
              </div>
              <Field label="两次抓取最小间隔(秒)">
                <Input
                  type="number"
                  min={0}
                  max={86400}
                  value={effective.minIntervalSec}
                  onChange={(e) => set('minIntervalSec', Number(e.target.value))}
                />
              </Field>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="third_party">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Plug className="h-4 w-4" /> 第三方爬虫服务
              </CardTitle>
              <CardDescription>
                你买的蝉妈妈 / 新榜 / 八爪鱼 / 自建。要求返回
                {' '}
                <code className="rounded bg-surface-variant px-1">[{`{rawId,title,url,score,metrics,raw}`}]</code>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field label="接口 URL" hint="GET,我们会带 Authorization: Bearer <token>">
                <Input
                  value={effective.thirdPartyUrl}
                  onChange={(e) => set('thirdPartyUrl', e.target.value)}
                  placeholder="https://your-vendor.example.com/hot/topics"
                />
              </Field>
              <Field label="Token">
                <Input
                  value={effective.thirdPartyToken}
                  onChange={(e) => set('thirdPartyToken', e.target.value)}
                  placeholder="留空保持不变;****  = 已配置"
                />
              </Field>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <p className="text-xs text-neutral-500">
        提示:配置只在当前进程生效,持久化请同步写入 <code className="rounded bg-surface-variant px-1">apps/api/.env</code>。
      </p>

      <ConfirmDialog
        open={!!pipelineConfirm}
        onOpenChange={(o) => {
          if (!o) setPipelineConfirm(null);
        }}
        tone={pipelineConfirm?.nextEnabled ? 'neutral' : 'danger'}
        title={pipelineConfirm?.nextEnabled ? '启用流水线?' : '关闭流水线?'}
        description={
          pipelineConfirm?.nextEnabled
            ? '启用后 Celery beat 会按计划自动调度,产生新的抓取请求。'
            : '关闭后 Celery beat 会跳过该任务,需要手动执行才能产生新数据。'
        }
        confirmText={pipelineConfirm?.nextEnabled ? '确认启用' : '确认关闭'}
        pending={togglePipeline.isPending}
        onConfirm={() => {
          if (!pipelineConfirm) return;
          togglePipeline.mutate(
            { id: pipelineConfirm.id, enabled: pipelineConfirm.nextEnabled },
            { onSettled: () => setPipelineConfirm(null) },
          );
        }}
      />

      <ConfirmDialog
        open={forceConfirm}
        onOpenChange={setForceConfirm}
        tone="danger"
        title="强制执行(忽略阈值)?"
        description="已超过今日硬阈值,强制再抓一次可能触发对方反爬,请确认无误后继续。"
        confirmText="我已知晓 · 强制执行"
        pending={runNow.isPending}
        onConfirm={() => {
          runNow.mutate(true, { onSettled: () => setForceConfirm(false) });
        }}
      />
    </div>
  );
}

function PipelineRow({
  item,
  toggling,
  onToggle,
}: {
  item: PipelineItem;
  toggling: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  const statusBadge = () => {
    if (item.status === 'disabled') return <Badge variant="default">已停用</Badge>;
    if (item.status === 'blocked') return <Badge variant="danger">阻断</Badge>;
    if (item.status === 'warning') return <Badge variant="warning">软警告</Badge>;
    return <Badge variant="success">运行中</Badge>;
  };
  const last = item.lastRunAt
    ? new Date(item.lastRunAt).toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '从未执行';
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-outline-variant/60 bg-surface-variant/30 p-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-neutral-800">{item.name}</div>
          {statusBadge()}
        </div>
        <p className="mt-0.5 text-xs text-neutral-500">{item.description}</p>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-neutral-500">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            每日 {String(item.cronHour).padStart(2, '0')}:
            {String(item.cronMinute).padStart(2, '0')}
          </span>
          <span>上次:{last}</span>
          <span>
            今日 {item.dailyUsed}/{item.dailyLimit} 次
          </span>
        </div>
      </div>
      <Button
        variant={item.enabled ? 'ghost' : 'agent'}
        agentColor="radar"
        size="sm"
        disabled={toggling}
        onClick={() => onToggle(!item.enabled)}
        title={item.enabled ? '关闭后 Celery beat 不再触发该流水线' : '开启自动抓取'}
      >
        <Power className="h-3.5 w-3.5" />
        {item.enabled ? '关闭' : '开启'}
      </Button>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <div className="text-xs font-medium text-neutral-700">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-neutral-500">{hint}</div>}
    </label>
  );
}
